<script setup lang="ts">
  import { Slider, Typo, TypoVariant, type SliderModelValue } from '@travelata-libs/ui-kit';
  import { computed } from 'vue';
  import type { NightsRange } from '@/shared/model';
  import { NIGHTS_RANGE_TEXTS } from '../config/texts';
  import { nightsWord } from '../lib/formatNightsLabel';
  import { MAX_NIGHTS, MIN_NIGHTS, normalizeRange } from '../lib/nightsRange';
  import { useNightsRange } from '../model/useNightsRange';

  interface NightsRangeSliderProps {
    modelValue?: NightsRange | null;
  }

  const props = withDefaults(defineProps<NightsRangeSliderProps>(), {
    modelValue: null,
  });

  interface NightsRangeSliderEmits {
    (e: 'update:modelValue', range: NightsRange): void;
    (e: 'manual'): void;
  }

  const emit = defineEmits<NightsRangeSliderEmits>();

  const model = computed<NightsRange>({
    get: () => normalizeRange(props.modelValue),
    set: (value: NightsRange) => emit('update:modelValue', value),
  });

  const core = useNightsRange({ model });

  const label = computed(() => {
    if (core.to.value === core.from.value) {
      return `${core.to.value} ${nightsWord(core.to.value)}`;
    }
    return `${NIGHTS_RANGE_TEXTS.sliderFrom}${core.from.value}${NIGHTS_RANGE_TEXTS.sliderTo}${
      core.to.value
    } ${nightsWord(core.to.value)}`;
  });

  const sliderValue = computed<SliderModelValue>(() => ({ min: core.from.value, max: core.to.value }));

  const onSliderUpdate = (value: SliderModelValue): void => {
    model.value = normalizeRange({ from: value.min, to: value.max });
    emit('manual');
  };
</script>

<template>
  <div
    class="nights-range-slider"
    data-testid="nights-range-slider"
  >
    <Typo
      class="nights-range-slider__label"
      :variant="TypoVariant.BodyLRegular"
      tag="span"
      data-testid="nights-range-slider-value"
    >
      {{ label }}
    </Typo>

    <Slider
      class="nights-range-slider__control"
      :model-value="sliderValue"
      :min="MIN_NIGHTS"
      :max="MAX_NIGHTS"
      range
      data-testid="nights-range-slider-control"
      @update:modelValue="onSliderUpdate"
    />
  </div>
</template>

<style scoped lang="scss">
  .nights-range-slider {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-s);
    width: 100%;

    &__label {
      color: var(--color-text-primary);
      text-align: center;
    }
  }
</style>
